<template>
    <div>
        <h1 class="display-heading">My Medicine Intake Reminders</h1>

        <div class="card mb-3">
            <div class="card-header">Create New Reminder</div>
            <div class="card-body">
                <form @submit.prevent="validateReminderForm">
                    <div class="form-group row">
                        <label class="col-sm-3 col-xl-2 col-form-label">Reminder Dates</label>
                        <div class="col-sm-9 col-xl-10">
                            <datetime-picker ref="reminderDatesRangerPicker" class="form-control"></datetime-picker>
                        </div>
                    </div>
                    <div class="form-group row">
                        <label class="col-sm-3 col-xl-2 col-form-label">Reminder Time</label>
                        <div class="col-sm-9 col-xl-10">
                            <select name="reminderTime" v-model="form.time" data-vv-as="reminder time"
                                data-vv-validate-on="blur" v-validate="'required'" :class="{
                                    'form-control': true,
                                    'is-invalid':  validationErrors.has('reminderTime')
                                }">
                                <option v-for="(time, index) in timeList" :key="index" :value="time.value">
                                    {{time.text}}
                                </option>
                            </select>
                            <div v-show="validationErrors.has('reminderTime')" class="invalid-feedback">
                                {{ validationErrors.first('reminderTime') }}
                            </div>
                        </div>
                    </div>
                     <div v-if="isSubmitMessageShown" class="row form-group">
                        <div class="col-sm-9 offset-sm-3 col-xl-10 offset-xl-2">
                            <p :class="{
                                'text-danger': isSubmitMessageError
                            }">
                                {{submitMessage}}
                            </p>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-sm-9 col-xl-10 offset-sm-3 offset-xl-2">
                            <button class="btn btn-primary btn-primary-insv mr-2 mb-2" type="button" @click="createReminders">
                                Create New Reminder
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>

        <div ref="tableSection">
            <div class="d-md-none">
                <div class="card mb-3" v-if="existingReminderList.length === 0">
                    <div class="card-body">
                        {{isTableLoading ? "Loading Data": "No Data"}}
                    </div>
                </div>

                <div class="card mb-3" v-for="(existingReminder, index) in existingReminderList" :key="index">
                    <div class="card-body">
                        <div class="mb-3">
                            <label class="label-display">
                                Start Date
                            </label>
                            <div>
                                {{existingReminder.startDate}}
                            </div>
                        </div>
                        <div class="mb-3">
                            <label class="label-display">
                                End Date
                            </label>
                            <div>
                                {{existingReminder.endDate}}
                            </div>
                        </div>
                        <div class="mb-3">
                            <label class="label-display">
                                Reminder Time
                            </label>
                            <div>
                                {{existingReminder.reminderTime}}
                            </div>
                        </div>
                        <div class="mb-3">
                            <label class="label-display">
                                Status
                            </label>
                            <div class="mt-2">
                                <span v-if="existingReminder.isExpired" class="badge badge-secondary">Expired</span>
                                <span v-else-if="!existingReminder.isActive" class="badge badge-secondary">Disabled</span>
                                <span v-else class="badge badge-success">Active</span>
                            </div>
                        </div>
                        <div>
                            <label class="label-display">
                                Action
                            </label>
                            <div class="mt-2">
                                <toggle-button :checkboxValue.sync="existingReminder.isActive"
                                    :disabled="existingReminder.isExpired"
                                    @update:checkboxValue="updateReminderStatus(existingReminder)">
                                </toggle-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="d-none d-md-block">
                <table ref="listTable" class="table table-hover">
                    <colgroup>
                        <col width="40">
                        <col>
                        <col>
                        <col>
                        <col>
                        <col>
                    </colgroup>
                    <thead>
                        <tr>
                            <th scope="col">#</th>
                            <th scope="col">Start Date</th>
                            <th scope="col">End Date</th>
                            <th scope="col">Reminder Time</th>
                            <th scope="col">Status</th>
                            <th scope="col">Action</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr v-show="existingReminderList.length === 0">
                            <td :colspan="columnsCount" class="text-center font-italic">
                                {{isTableLoading ? "Loading Data": "No Data"}}
                            </td>
                        </tr>
                        <tr v-for="(existingReminder, index) in existingReminderList" :key="index">

                            <th scope="row">{{((pagination.currentPage - 1) *pagination.pageSize ) + index + 1}}</th>
                            <td class="text-nowrap">
                                {{existingReminder.startDate}}
                            </td>
                            <td class="text-nowrap">
                                {{existingReminder.endDate}}
                            </td>
                            <td class="text-nowrap">
                                {{existingReminder.reminderTime}}
                            </td>
                            <td>
                                <span v-if="existingReminder.isExpired" class="badge badge-secondary">Expired</span>
                                <span v-else-if="!existingReminder.isActive" class="badge badge-secondary">Disabled</span>
                                <span v-else class="badge badge-success">Active</span>
                            </td>
                            <td>
                                <toggle-button :checkboxValue.sync="existingReminder.isActive"
                                    :disabled="existingReminder.isExpired"
                                    @update:checkboxValue="updateReminderStatus(existingReminder)">
                                </toggle-button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <pagination v-model="pagination.currentPage"
                :totalPages="pagination.totalPages"
                :scrollPosition="pagination.scrollPosition">
            </pagination>
        </div>

         <popup-modal ref="popupUpdateSuccessful" :hasCloseButton="false" title="Update Success!">
            <template slot="content">
                <p>{{successMessage}}</p>
            </template>
            <template slot="footer">
                <button type="button" class="btn btn-secondary" @click="dismissPopupSuccessful">Okay</button>
            </template>
        </popup-modal>
    </div>
</template>

<script>
import moment from 'moment';
import popupModal from '@/components/popup-modal';
import toggleButton from '@/components/toggle-button';
import pagination from '@/components/pagination';
import { convertToUserReadableDate, convertToUserReadableTime } from '@/utilities/';
import datetimePicker from '@/components/datetime-picker';
import timeDropdown from '@/config/timeDropdown';
export default {
    'name': 'MedicineIntakeReminders',
    'components': {
        datetimePicker,
        toggleButton,
        popupModal,
        pagination
    },
    data () {
        return {
            'timeList': [],
            'form': {
                'time': null
            },
            'existingReminderList': [],
            'columnsCount': 1,
            'isTableLoading': true,
            'isSubmitMessageShown': false,
            'isSubmitMessageError': false,
            'submitMessage': '',
            'successMessage': 'Reminder has been turned on successfully.',
            'pagination': {
                'currentPage': 1,
                'totalPages': 0,
                'pageSize': 20,
                'scrollPosition': 0
            }
        };
    },
    'computed': {
        currentPage () {
            return this.pagination.currentPage;
        }
    },
    'watch': {
        currentPage () {
            this.$nextTick(() => {
                this.getReminders();
            });
        }
    },
    mounted () {
        this.$nextTick(() => {
        // initialize scroll position
            this.pagination.scrollPosition = this.$refs.tableSection.getBoundingClientRect().top + window.scrollY - document.querySelector('.top-navigation').clientHeight;
        });

        this.$refs.reminderDatesRangerPicker.setDate(moment(), moment().add(7, 'days'));
        this.columnsCount = this.$refs.listTable.rows[0].cells.length;
        this.timeList = timeDropdown(30);

        this.getReminders();
    },
    'methods': {
        validateReminderForm () {
            return this.$validator.validateAll();
        },
        createReminders () {
            this.isSubmitMessageShown = true;
            this.isSubmitMessageError = false;

            this.validateReminderForm().then((isValid) => {
                if (isValid) {
                    let { startDate, endDate } = this.$refs.reminderDatesRangerPicker.getDates();
                    this.submitMessage = 'Creating new reminders...';
                    this.axios.post('Reminder/CreateMedicineIntakeReminder', {
                        'sd': startDate.toISOString(true),
                        'ed': endDate.toISOString(true),
                        'nt': this.form.time
                    }).then(() => {
                        this.submitMessage = 'Done creating new reminder!';
                        this.form.time = null;
                        this.getReminders();
                    }).catch((error) => {
                        this.isSubmitMessageError = true;
                        if (error.response && error.response.data) {
                            this.submitMessage = error.response.data.message;
                        } else {
                            this.submitMessage = 'Error creating new reminder. Please try again.';
                        }
                    });
                } else {
                    this.isSubmitMessageError = true;
                    this.submitMessage = 'You have error in the form. Please check your input(s) before submitting the form.';
                }
            });
        },
        getReminders () {
            this.axios.get('Reminder/GetMyReminders', {
                'params': {
                    'page': this.pagination.currentPage,
                    'pageSize': this.pagination.pageSize
                }
            }).then(({ data }) => {
                this.isTableLoading = false;
                let pagedResult = data.data;

                this.pagination.currentPage = pagedResult.p;
                this.pagination.totalPages = pagedResult.c;

                let list = pagedResult.l;
                this.existingReminderList = list.map((reminder) => ({
                    'reminderId': reminder.ri,
                    'startDate': convertToUserReadableDate(reminder.sd),
                    'endDate': convertToUserReadableDate(reminder.ed),
                    'reminderTime': convertToUserReadableTime(reminder.nt),
                    'createDate': convertToUserReadableDate(reminder.cd),
                    'isActive': reminder.ia,
                    'isExpired': reminder.ie
                }));
            });
        },
        updateReminderStatus (reminder) {
            if (reminder) {
                if (reminder.isActive) {
                    this.axios.get('Reminder/EnableReminder', {
                        'params': {
                            'reminderId': reminder.reminderId
                        }
                    }).then(() => {
                        this.successMessage = 'Reminder has been turned on successfully.';
                    });
                } else {
                    this.axios.get('Reminder/DisableReminder', {
                        'params': {
                            'reminderId': reminder.reminderId
                        }
                    }).then(() => {
                        this.successMessage = 'Reminder has been turned off successfully.';
                    });
                }

                this.$nextTick(() => {
                    this.$refs.popupUpdateSuccessful.show();
                });
            }
        },
        dismissPopupSuccessful () {
            this.$refs.popupUpdateSuccessful.hide();
        }
    }
};

</script>

<style>

</style>
